// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { FIELD_METADATA } from '../types/policyMetadata';

export type FieldSource = 'structured' | 'provider' | 'none';

export interface MergedFieldValue {
  value: unknown;
  source: FieldSource;
}

/**
 * Provider formData key → structured details field key mapping.
 * Maps provider submission field names to the canonical field names used in structured details.
 */
const PROVIDER_FIELD_MAP: Record<string, string> = {
  // Provider information
  providerContactName: 'providerContactName',
  completedByName: 'completedByName',
  completedByEmail: 'completedByEmail',
  completionDate: 'completionDate',

  // Policyholder details
  policyholderName: 'customerName',
  policyholderEmail: 'customerEmail',
  policyholderPhone: 'customerPhone',
  policyholderDateOfBirth: 'customerDateOfBirth',
  policyholderAddressLine1: 'customerAddress',
  policyholderAddressLine2: 'customerAddressLine2',
  policyholderAddressLine3: 'customerAddressLine3',
  policyholderCity: 'customerCity',
  policyholderStateProvince: 'customerStateProvince',
  policyholderPostalCode: 'customerPostalCode',
  policyholderCountry: 'customerCountry',

  // Policy details
  policyType: 'pensionType',
  pensionType: 'pensionType',
  fullPolicyNumber: 'policyAccountNumber',
  currentValue: 'policyValue',
  currentValueDate: 'currentValueDate',
  transferValue: 'transferValue',
  transferValueDate: 'transferValueDate',
  schemeRevenueRefNumber: 'schemeRevenueRefNumber',
  policyStatus: 'policyStatus',
  policyClosedReason: 'closedReason',
  // Closed sub-fields: retired prefix
  retiredProviderName: 'closedProviderName',
  retiredDateOfTransfer: 'closedDateOfTransfer',
  retiredPolicyReference: 'closedPolicyReference',
  retiredPolicyType: 'closedPolicyType',
  retiredTransferType: 'closedTransferType',
  retiredTransferValue: 'closedTransferValue',
  retiredBceDate: 'retiredBceDate',
  retiredBceValue: 'retiredBceValue',
  retiredTaxFreeLumpSum: 'retiredTaxFreeLumpSum',
  retiredPreviousTaxFreeLumpSums: 'retiredPreviousTaxFreeLumpSums',
  // Closed sub-fields: transferred prefix
  transferredProviderName: 'closedProviderName',
  transferredDateOfTransfer: 'closedDateOfTransfer',
  transferredPolicyReference: 'closedPolicyReference',
  transferredPolicyType: 'closedPolicyType',
  transferredTransferType: 'closedTransferType',
  transferredTransferValue: 'closedTransferValue',
  totalEmployerContributions: 'closedTotalEmployerContributions',
  totalEmployeeContributions: 'closedTotalEmployeeContributions',

  // Employment details
  employerName: 'employerName',
  dateJoinedScheme: 'dateJoinedScheme',
  firstDateOfEmployment: 'firstDateOfEmployment',
  schemePolicyNra: 'schemeNormalRetirementAge',
  dateOfLeavingService: 'dateOfLeavingService',
  salary: 'salaryAtDateOfLeaving',
  severancePackageReceived: 'severancePackageReceived',
  severanceWaivedLumpSum: 'severanceWaivedLumpSum',
  severanceNotes: 'severanceNotes',

  // Contribution details
  employerContribution: 'employerContribution',
  employeeContribution: 'employeeContribution',
  payingStatus: 'payingStatus',
  dateOfLastContribution: 'dateOfLastContribution',
  regularPremium: 'regularPremiumAmount',
  regularPremiumFrequency: 'regularPremiumFrequency',
  singlePremium: 'singlePremiumAmount',
  singlePremiumFrequency: 'singlePremiumFrequency',
  indexationDetails: 'indexationDetails',
  policyPaidBy: 'policyPaidBy',
  dueDateNextPayment: 'dueDateNextPayment',
  dueAmountNextPayment: 'dueAmountNextPayment',
  transfersInFromAnotherScheme: 'transfersInFromAnotherScheme',

  // Transfers In (direct mapping — wizard field names match metadata field names)
  hasReceivedTransfers: 'hasTransferFromOtherSchemes',
  detailAmountTransferred: 'detailAmountTransferred',
  detailProvider: 'detailProvider',
  detailDateTransferred: 'detailDateTransferred',
  detailPolicyType: 'detailPolicyType',
  detailReferenceNumber: 'detailReferenceNumber',
  detailSchemeName: 'detailSchemeName',
  sameEmployment: 'sameEmployment',
  sameEmpDateTransferred: 'sameEmpDateTransferred',
  sameEmpAmountTransferred: 'sameEmpAmountTransferred',
  employeeContributionsTransferred: 'employeeContributionsTransferred',
  employerContributionsTransferred: 'employerContributionsTransferred',
  previousProviderName: 'transferPreviousProviderName',
  previousPolicyNumber: 'transferPreviousPolicyNumber',
  transferType: 'transferType',
  previousProvidersName: 'previousProvidersName',
  previousPolicyNumberNo: 'previousPolicyNumberNo',
  employerDetails: 'employerDetails',
  transferDateJoinedService: 'transferDateJoinedService',
  transferDateJoinedScheme: 'transferDateJoinedScheme',
  transferDateOfLeavingService: 'transferDateOfLeavingService',
  salaryAtLeavingService: 'salaryAtLeavingService',
  // totalEmployerContributions/totalEmployeeContributions mapped at line 63-64 to closedTotal* (policy status context)
  // For transfers context, the wizard uses these same keys — handled by direct field name in section data
  transferFromOverseas: 'transferFromOverseas',
  overseasCountry: 'overseasCountry',
  wasTaxPaid: 'wasTaxPaid',
  overseasTaxDetails: 'overseasTaxDetails',
  transferTaxBackupUpload: 'transferTaxBackupUpload',

  // Crystallised / Retired
  crystallisedStatus: 'hasBeenCrystallised',
  crystallisedBceDate: 'crystallisedDate',
  crystallisedBceValue: 'crystallisedAmount',
  crystallisedProviderName: 'crystallisedProviderName',
  crystallisedQfmName: 'crystallisedQfmName',
  crystallisedTaxFreeLumpSum: 'taxFreeLumpSum',
  crystallisedTaxFreeLumpSumDate: 'taxFreeLumpSumDate',
  crystallisedOtherLumpSum: 'otherLumpSum',
  crystallisedTaxPaidOtherLumpSum: 'taxPaidOtherLumpSum',
  crystallisedOtherLumpSumDate: 'otherLumpSumDate',
  crystallisedAdditionalLumpSums: 'additionalLumpSums',
  crystallisedDocumentUpload: 'crystallisedDocumentUpload',
  crystallisedDrawingIncome: 'isDrawingIncome',
  crystallisedAnnualDrawdownPercent: 'annualDrawdownPercent',
  crystallisedLastAnnualDrawdown: 'lastAnnualDrawdown',
  crystallisedDrawdownFrequency: 'drawdownFrequency',
  crystallisedDrawdownDates: 'drawdownPaymentDate',

  // Investments (array preserved directly)
  investmentFunds: 'investmentFunds',

  // Charges & Fees
  brokerAdvisoryAmc: 'brokerAdvisoryAmc',
  commissionsOnPolicy: 'commissionsOnPolicy',
  allocationPercentContributions: 'allocationPercentContributions',
  exitChargesDetails: 'exitChargesPenalties',
  transferOutCosts: 'transferOutCosts',
  guaranteedReturnsDetails: 'guaranteedReturns',
  terminalBonusDetails: 'terminalBonusDetails',
  otherChargeDetails: 'otherChargeDetails',
  chargesFeesDocUpload: 'chargesFeesDocUpload',

  // Additional information
  policyAssigned: 'hasPolicyBeenAssigned',
  policyAssignedTo: 'policyAssignedTo',
  policyAssignedDate: 'policyAssignedDate',
  policyAssignedDetails: 'policyAssignedDetails',
  assignedDocUpload: 'assignedDocUpload',
  pensionAdjustmentOrder: 'hasPensionAdjustmentOrder',
  pensionAdjustmentDate: 'pensionAdjustmentDate',
  pensionAdjustmentDetails: 'pensionAdjustmentDetails',
  pensionAdjDocUpload: 'pensionAdjustmentOrderDocument',
  uniqueDeathBenefit: 'hasUniqueDeathBenefit',
  uniqueDeathBenefitExpiry: 'uniqueDeathBenefitExpiry',
  uniqueDeathBenefitDetails: 'uniqueDeathBenefitDetails',

  // Transaction History
  transactionHistoryUpload: 'transactionHistoryUpload',
  currentValuationUpload: 'currentValuationUpload',
  sorpUpload: 'sorpUpload',

  // Investment-specific fields
  investmentType: 'investmentType',
  typeOfOwnership: 'typeOfOwnership',
  typeOfAccount: 'accountType',
  riskLevel: 'riskLevel',
  namesOfLifeAssured: 'namesOfLifeAssured',
  policyStartDate: 'policyStartDate',
  amountInvested: 'amountInvested',
  currentPolicyValue: 'currentPolicyValue',
  dateCurrentPolicyValue: 'dateCurrentPolicyValue',
  exitPenaltyDetails: 'exitPenaltyDetails',
  exitTaxesPayable: 'exitTaxesPayable',
  transferSurrenderValue: 'transferSurrenderValue',
  dateTransferSurrenderValue: 'dateTransferSurrenderValue',
  maturityDate: 'maturityDate',
  premiumAmount: 'premiumAmount',
  premiumFrequency: 'premiumFrequency',
  otherNotes: 'otherNotes',
  hasBareTrustOrDeed: 'hasBareTrustOrDeed',
  bareTrustOrDeedTo: 'bareTrustOrDeedTo',

  // Associated Protection Benefits (arrays preserved directly)
  hasProtectionBenefits: 'hasProtectionBenefits',
  lifeCoverEnabled: 'lifeCoverEnabled',
  lifeCoverCards: 'lifeCoverCards',
  incomeProtectionEnabled: 'incomeProtectionEnabled',
  incomeProtectionCards: 'incomeProtectionCards',
  specifiedIllnessEnabled: 'specifiedIllnessEnabled',
  specifiedIllnessCards: 'specifiedIllnessCards',
};

/**
 * Maps for converting complex provider array fields to text metadata fields.
 */
const PROVIDER_ARRAY_FIELD_MAP: Record<string, { target: string; formatter: (v: unknown) => string }> = {
  // investmentFunds, benefit cards: mapped directly via PROVIDER_FIELD_MAP to preserve array structure
};

/**
 * Coerce a string value to the appropriate type based on field metadata.
 */
function coerceValue(fieldKey: string, value: unknown): unknown {
  if (value === null || value === undefined || value === '') return value;

  const meta = FIELD_METADATA[fieldKey];
  if (!meta) return value;

  const strValue = String(value);

  switch (meta.type) {
    case 'number': {
      const num = Number.parseFloat(strValue);
      return Number.isNaN(num) ? strValue : num;
    }
    case 'boolean':
      if (typeof value === 'boolean') return value;
      return strValue === 'true' || strValue === 'yes' || strValue === '1';
    default:
      return value;
  }
}

function isPresent(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function buildProviderValueMap(
  providerFormData: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const providerValues: Record<string, unknown> = {};
  if (!providerFormData) return providerValues;
  for (const [provKey, provValue] of Object.entries(providerFormData)) {
    if (!isPresent(provValue)) continue;
    const arrayMapping = PROVIDER_ARRAY_FIELD_MAP[provKey];
    if (arrayMapping) {
      const formatted = arrayMapping.formatter(provValue);
      if (formatted) providerValues[arrayMapping.target] = formatted;
      continue;
    }
    const canonicalKey = PROVIDER_FIELD_MAP[provKey] || provKey;
    providerValues[canonicalKey] = provValue;
    if (canonicalKey !== provKey) providerValues[provKey] = provValue;
  }
  return providerValues;
}

function resolveMergedValue(
  fieldKey: string,
  structuredFields: Record<string, unknown>,
  providerValues: Record<string, unknown>
): MergedFieldValue {
  const structuredValue = structuredFields[fieldKey];
  if (isPresent(structuredValue)) {
    return { value: structuredValue, source: 'structured' };
  }
  const providerValue = providerValues[fieldKey];
  if (isPresent(providerValue)) {
    return { value: coerceValue(fieldKey, providerValue), source: 'provider' };
  }
  return { value: undefined, source: 'none' };
}

/**
 * Merge structured details fields with provider form data.
 *
 * - Structured details values take precedence when non-null/non-empty.
 * - Provider formData fills in gaps using PROVIDER_FIELD_MAP.
 * - Returns merged values with provenance info per field.
 */
export function mergeMetadataFields(
  structuredFields: Record<string, unknown>,
  providerFormData: Record<string, unknown> | null | undefined
): Record<string, MergedFieldValue> {
  const providerValues = buildProviderValueMap(providerFormData);
  const result: Record<string, MergedFieldValue> = {};
  for (const fieldKey of Object.keys(FIELD_METADATA)) {
    result[fieldKey] = resolveMergedValue(fieldKey, structuredFields, providerValues);
  }
  return result;
}

/**
 * Extract flat field values from merged result for form state.
 */
export function extractFieldValues(merged: Record<string, MergedFieldValue>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, { value }] of Object.entries(merged)) {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  }
  return result;
}
